<template>
    <!-- BOOKING DETAILS HEADER -->
    <div class="pt-16 pb-6 flex-col justify-center items-start gap-1">
        <h2 class="self-stretch text-slate-800 text-lg leading-7 font-medium">
            Available Award Points
        </h2>
        <p class="self-stretch text-slate-500 text-sm leading-tight">
            <span v-html="property.name"></span>
            participates in the
            {{ $filters.ucWords(selectedProgram?.name) }}
            program, and can be booked with
            {{ $filters.ucWords(selectedProgram?.name) }}
            points.
        </p>
    </div>
    <!-- BOOKING DETAILS CONTAINER -->
    <div class="w-full relative overflow-hidden">
        <div v-if="loading" class="h-48 bg-white rounded-lg">
            <LoadingState :loading="loading" />
        </div>
        <div
            v-else
            class="w-full bg-white rounded-lg p-1 shadow grid grid-cols-12 gap-1 @container"
        >
            <div class="col-span-12 @3xl:col-span-4 relative">
                <!-- START BOOKING INFO CONTAINER: MY POINTS -->
                <BookingInfoContainer
                    :heading="`My ${$filters.ucWords(property.program?.name)} Points`"
                    width="29%"
                >
                    <PropertyCardMyPoints
                        :programImage="property.program?.image"
                        :pointsForProgram="pointsForProgram"
                        orientation="horizontal"
                    />
                </BookingInfoContainer>
                <!-- END BOOKING INFO CONTAINER: MY POINTS -->
                <!-- START PLUS GRAPHIC -->
                <div
                    class="absolute z-10 left-1/2 -translate-x-1/2 -bottom-[20px] @3xl:left-auto @3xl:bottom-auto @3xl:translate-x-0 @3xl:top-1/2 @3xl:-translate-y-1/2 @3xl:-right-[22px]"
                    aria-hidden="true"
                >
                    <div
                        class="w-10 h-10 bg-white rounded-full shadow border border-slate-200 justify-center items-center gap-2.5 flex text-slate-500"
                    >
                        <i class="fa-duotone fa-plus"></i>
                    </div>
                </div>
            </div>
            <!-- END PLUS GRAPHIC -->
            <!-- START BOOKING INFO CONTAINER: ADDITIONAL OPTIONS -->
            <div class="col-span-12 @3xl:col-span-4 relative">
                <BookingInfoContainer
                    heading="Transfer Partner Options"
                    width="42%"
                >
                    <PropertyCardTransferOptions
                        v-if="filteredUserPoints.length"
                        :programName="$filters.ucWords(property.group)"
                        :programImage="property.program?.image"
                        :sourceProgramUserPoints="filteredUserPoints"
                    />
                    <p v-else class="text-slate-500 text-sm font-normal">
                        None of your current points transfer to
                        {{ selectedProgram?.name }}.
                    </p>
                </BookingInfoContainer>
                <!-- END BOOKING INFO CONTAINER: ADDITIONAL OPTIONS -->
                <!-- START ARROW GRAPHIC -->
                <div
                    class="absolute left-1/2 -translate-x-1/2 -bottom-[20px] @3xl:left-auto @3xl:bottom-auto @3xl:translate-x-0 @3xl:top-1/2 @3xl:-translate-y-1/2 @3xl:-right-[22px] z-10"
                    aria-hidden="true"
                >
                    <div
                        class="w-10 h-10 bg-white rounded-full shadow border border-slate-200 justify-center items-center gap-2.5 flex text-slate-500"
                    >
                        <i
                            class="fa-light fa-arrow-right rotate-90 @3xl:rotate-0"
                        ></i>
                    </div>
                </div>
            </div>
            <!-- END ARROW GRAPHIC -->
            <!-- START BOOKING INFO CONTAINER: STAY PRICE -->
            <div class="col-span-12 @3xl:col-span-4">
                <BookingInfoContainer
                    heading="Points Needed for Stay"
                    width="29%"
                >
                    <!-- Start need more points card -->
                    <CardRecommendation
                        :program="selectedProgram"
                        :usersWhoNeedCardRecs="pointsForProgram"
                    />
                    <!-- end need more points card -->
                </BookingInfoContainer>
            </div>
            <!-- END BOOKING INFO CONTAINER: STAY PRICE -->
        </div>
    </div>
</template>
<script>
import { ref, computed } from 'vue';
import { useRewardsAndTransferPartners } from '@/composables/useRewardsAndTransferPartners.vue';

import { useHotelsStore } from '@/store/hotels';
import { usePointsStore } from '@/store/points';
import BookingInfoContainer from '@/components/hotels/booking-info-container/booking-info-container.vue';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import PropertyCardMyPoints from '@/components/hotels/search/property-card/property-card-my-points/property-card-my-points.vue';
import PropertyCardTransferOptions from '@/components/hotels/search/property-card/property-card-transfer-options.vue';
import CardRecommendation from '@/components/hotels/search/property-card/card-recommendation/card-recommendation.vue';

export default {
    components: {
        BookingInfoContainer,
        CardRecommendation,
        Icon,
        LoadingState,
        PropertyCardMyPoints,
        PropertyCardTransferOptions,
    },
    setup() {
        const hotelsStore = useHotelsStore();
        const pointsStore = usePointsStore();

        const { filterUserPoints, initializePointsAndTransfersData, loading } =
            useRewardsAndTransferPartners();

        const property = computed(() => hotelsStore.property);

        const noTransferPointsAvailable = ref(false);

        const filteredUserPoints = computed(() => {
            return filterUserPoints(property.value.program?.id);
        });

        const selectedProgram = computed(() => hotelsStore.pointsProgram);

        const pointsForProgram = computed(() => {
            return pointsStore.filterDataByProgramId(
                property.value.program?.id
            );
        });

        const initializeData = async () => {
            await initializePointsAndTransfersData();
        };

        initializeData();

        return {
            filteredUserPoints,
            loading,
            noTransferPointsAvailable,
            property,
            pointsForProgram,
            selectedProgram,
        };
    },
};
</script>
